<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import {
    UiPredictiveSearchCombobox,
    UiButton,
    UiText,
    UiIcon,
    PartnerChip,
    generateInitials,
    type ComboboxItemData,
} from '@shopexperts/shared';
import { useExpertSearch } from '@/app/composables/badges/useExpertSearch';
import { useSyncBadgesToExpert } from '@/app/composables/badges/useSyncBadgesToExpert';
import { useApi } from '@shopexperts/shared';

type ExpertQuickSearchResult = Awaited<ReturnType<ReturnType<typeof useApi>["admin"]["expert"]["quickSearch"]>>[number];

interface AssignExpertFormProps {
    badgeId: number;
    badgeName: string;
}

const props = defineProps<AssignExpertFormProps>();

const emit = defineEmits<{
    (e: 'success'): void;
}>();

const searchQuery = ref('');
const selectedExpertId = ref<string | undefined>();
const selectedExpert = ref<ExpertQuickSearchResult | null>(null);

const { data, isSearching, hasResults } = useExpertSearch(searchQuery);
const { mutateAsync: syncBadges, isPending: isAssigning } = useSyncBadgesToExpert();

// Transform expert search results to combobox items
const searchResults = computed<ComboboxItemData[]>(() => {
    return data.value?.map((expert) => ({
        value: expert.id.toString(),
        label: expert.name,
        image: expert.avatar || undefined,
        rounded: true,
        description: expert.email || '',
        initials: generateInitials(expert.name),
        showImage: true,
        metadata: {
            badges: expert.badges,
        },
    })) || [];
});

const showNoMatchMessage = computed(() => {
    return searchQuery.value.length >= 2 &&
        !isSearching.value &&
        !hasResults.value &&
        !selectedExpert.value;
});

// Watch for expert selection
watch(selectedExpertId, (newExpertId) => {
    if (newExpertId) {
        const expert = data.value?.find(e => e.id.toString() === newExpertId);
        if (expert) {
            selectedExpert.value = expert;
        }
    } else {
        selectedExpert.value = null;
    }
});

// Current expert's badges for display
const currentBadges = computed(() => {
    return selectedExpert.value?.badges || [];
});

// Check if badge is already assigned
const isBadgeAlreadyAssigned = computed(() => {
    return currentBadges.value.some(badge => badge.id === props.badgeId);
});

async function onRemoveBadge(badgeIdToRemove: number) {
    if (!selectedExpert.value) return;

    try {
        // Get current badge IDs and filter out the one to remove
        const currentBadgeIds = currentBadges.value.map(b => b.id);
        const updatedBadgeIds = currentBadgeIds.filter(id => id !== badgeIdToRemove);

        await syncBadges({
            expertId: selectedExpert.value.id,
            badge_ids: updatedBadgeIds,
        });

        // Update the local selected expert to reflect the change
        if (selectedExpert.value) {
            selectedExpert.value = {
                ...selectedExpert.value,
                badges: selectedExpert.value.badges.filter(b => b.id !== badgeIdToRemove)
            };
        }
    } catch (error) {
        console.error('[AssignExpertForm] Error removing badge:', error);
    }
}

async function onAssign() {
    if (!selectedExpert.value) return;

    try {
        // Get current badge IDs
        const currentBadgeIds = currentBadges.value.map(b => b.id);

        // Add new badge if not already present
        const updatedBadgeIds = isBadgeAlreadyAssigned.value
            ? currentBadgeIds
            : [...currentBadgeIds, props.badgeId];

        // Remove duplicates just in case
        const uniqueBadgeIds = [...new Set(updatedBadgeIds)] as number[];

        await syncBadges({
            expertId: selectedExpert.value.id,
            badge_ids: uniqueBadgeIds,
        });

        // Clear selection and prepare for next assignment
        searchQuery.value = '';
        selectedExpertId.value = undefined;
        selectedExpert.value = null;

        emit('success');
    } catch (error) {
        console.error('[AssignExpertForm] Error assigning badge:', error);
    }
}

defineExpose({
    resetForm: () => {
        searchQuery.value = '';
        selectedExpertId.value = undefined;
        selectedExpert.value = null;
    },
});
</script>

<template>
    <div class="flex flex-col gap-4">
        <!-- Expert Search -->
        <div>
            <UiText as="label" variant="p" class="font-semibold mb-2 block">
                Search Expert
            </UiText>
            <UiPredictiveSearchCombobox v-model="searchQuery" v-model:selected-id="selectedExpertId"
                :data="searchResults" :is-fetching="isSearching" :show-search-icon="false" mode="preserve"
                hide-combobox-when-empty placeholder="Search experts by name or email..." />

            <UiText v-if="showNoMatchMessage" as="p" variant="p-small" class="text-gray-600 mt-2">
                No experts found matching your search.
            </UiText>
        </div>

        <!-- Selected Expert's Current Badges -->
        <div v-if="selectedExpert" class="space-y-2">
            <UiText as="label" variant="p" class="font-semibold">
                {{ selectedExpert.name }}'s Current Badges
            </UiText>

            <div v-if="currentBadges.length > 0" class="flex flex-wrap gap-2">
                <PartnerChip v-for="badge in currentBadges" :key="badge.id" :name="badge.name"
                    :icon-url="badge.icon?.cdn_url || badge.icon?.url">
                    <template #inline-after>
                        <UiButton @click.stop="onRemoveBadge(badge.id)" variant="ghost" shape="icon" :disabled="isAssigning"
                            class="hover:bg-se-danger/10 rounded-full transition-colors disabled:opacity-50 translate-x-1/4"
                            :title="`Remove ${badge.name}`">
                            <template #before_label>
                                <UiIcon name="close" class="w-4 h-4 text-se-dark " />
                            </template>
                        </UiButton>
                    </template>
                </PartnerChip>
            </div>

            <UiText v-else as="p" variant="p-small" class="text-gray-500">
                No badges assigned yet
            </UiText>

            <!-- Warning if badge already assigned -->
            <div v-if="isBadgeAlreadyAssigned" class="p-3 bg-amber-50 border border-amber-200 rounded">
                <UiText as="p" variant="p-small" class="text-amber-800">
                    This expert already has the "{{ badgeName }}" badge assigned.
                </UiText>
            </div>
        </div>

        <!-- Submit Button -->
        <div class="flex justify-end pt-2">
            <UiButton type="button" variant="primary" label="Assign Badge" :disabled="!selectedExpert || isAssigning || isBadgeAlreadyAssigned"
                @click="onAssign" />
        </div>
    </div>
</template>
